import { cn } from '@commenda/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { LoadingIndicator } from '../LoadingIndicator';

// Rest styles only. Hover lives in `buttonHoverVariants` so `noHover` can
// suppress it without `!important`. Adding a variant here? Add hover too.
const buttonRestVariants = cva(
  'inline-flex justify-center items-center w-fit rounded-md text-center outline-none whitespace-nowrap transition-colors disabled:pointer-events-none disabled:cursor-not-allowed focus-visible:outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 box-border',
  {
    variants: {
      variant: {
        'primary-brown':
          'bg-button-primary-bg disabled:bg-button-primary-bg-disabled text-button-primary-text disabled:text-button-primary-text-disabled focus-visible:ring-fg-tertiary',
        primary:
          'bg-button-primary-brand-bg disabled:bg-button-primary-bg-disabled text-button-primary-text disabled:text-button-primary-text-disabled focus-visible:ring-fg-tertiary',
        secondary:
          'bg-button-secondary-bg disabled:bg-bg-primary text-button-secondary-text disabled:text-button-secondary-text-disabled border border-button-primary-border disabled:border-button-secondary-border-disabled focus-visible:ring-fg-tertiary',
        tertiary:
          'bg-transparent disabled:bg-transparent text-button-tertiary-text disabled:text-button-tertiary-text-disabled focus-visible:ring-fg-tertiary',
        link: 'text-button-link-text disabled:text-button-link-text-disabled focus-visible:ring-fg-tertiary border-b border-b-[#84807A] disabled:border-b-border-disabled',
        'brand-link':
          'text-fg-brand disabled:text-text-disabled focus-visible:ring-fg-tertiary',
        'destructive-primary':
          'bg-button-primary-error-bg disabled:bg-button-primary-error-bg-disabled text-button-primary-error-text disabled:text-button-primary-error-text-disabled focus-visible:ring-bg-error-solid',
        'destructive-secondary':
          'bg-button-secondary-error-bg disabled:bg-bg-primary text-button-secondary-error-text disabled:text-button-secondary-error-text-disabled border border-button-secondary-error-border disabled:border-button-secondary-error-border-disabled focus-visible:ring-bg-error-solid',
        'destructive-tertiary':
          'bg-transparent disabled:bg-transparent text-button-tertiary-error-text disabled:text-button-tertiary-error-text-disabled focus-visible:ring-bg-error-solid',
        'destructive-link':
          'text-text-error disabled:text-text-disabled focus-visible:ring-bg-error-solid border-b border-b-border-error disabled:border-b-border-disabled',
      },
      size: {
        xs: 'py-xs px-md gap-xs txt-extra-small-medium h-7 rounded-sm',
        sm: 'py-sm px-md gap-sm txt-small-medium h-8 rounded-sm',
        md: 'py-md px-lg gap-sm txt-small-medium h-9',
        lg: 'py-md px-lg gap-md txt-medium-medium h-10',
        xl: 'py-lg px-xl gap-md txt-medium-medium h-12',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    compoundVariants: [
      { variant: 'link', class: '!px-0 !py-xxs' },
      { variant: 'destructive-link', class: '!px-0 !py-xxs' },
      { variant: 'brand-link', class: '!px-0 !py-xxs' },
      { variant: 'link', size: 'xs', class: 'h-5' },
      { variant: 'link', size: 'sm', class: 'h-6' },
      { variant: 'link', size: 'md', class: 'h-7' },
      { variant: 'link', size: 'lg', class: 'h-8' },
      { variant: 'link', size: 'xl', class: 'h-8' },
      { variant: 'destructive-link', size: 'xs', class: 'h-5' },
      { variant: 'destructive-link', size: 'sm', class: 'h-6' },
      { variant: 'destructive-link', size: 'md', class: 'h-7' },
      { variant: 'destructive-link', size: 'lg', class: 'h-8' },
      { variant: 'destructive-link', size: 'xl', class: 'h-8' },
      { variant: 'brand-link', size: 'xs', class: 'h-5' },
      { variant: 'brand-link', size: 'sm', class: 'h-6' },
      { variant: 'brand-link', size: 'md', class: 'h-7' },
      { variant: 'brand-link', size: 'lg', class: 'h-8' },
      { variant: 'brand-link', size: 'xl', class: 'h-8' },
      { variant: 'link', class: 'rounded-none' },
      { variant: 'destructive-link', class: 'rounded-none' },
      { variant: 'brand-link', class: 'rounded-none' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

const buttonHoverVariants = cva('', {
  variants: {
    variant: {
      'primary-brown':
        'hover:bg-button-primary-bg-hover hover:text-button-primary-text-hover',
      primary:
        'hover:bg-button-primary-brand-bg-hover hover:text-button-primary-text-hover',
      secondary:
        'hover:bg-button-secondary-bg-hover hover:text-button-secondary-text-hover hover:border-button-secondary-border-hover',
      tertiary:
        'hover:bg-button-tertiary-bg-hover hover:text-button-tertiary-text-hover',
      link: 'hover:text-button-link-text-hover hover:border-b-[#57524D]',
      'brand-link': 'hover:text-fg-brand/80',
      'destructive-primary':
        'hover:bg-button-primary-error-bg-hover hover:text-button-primary-error-text-hover',
      'destructive-secondary':
        'hover:bg-button-secondary-error-bg-hover hover:text-button-secondary-error-text-hover hover:border-button-secondary-error-border-hover',
      'destructive-tertiary':
        'hover:bg-button-tertiary-error-bg-hover hover:text-button-tertiary-error-text-hover',
      'destructive-link':
        'hover:text-text-error-hover hover:border-b-border-error-hover',
    },
  },
  defaultVariants: { variant: 'primary' },
});

/**
 * Public class-string builder. Same call signature as before — direct
 * consumers (anchor tags styled like buttons, custom button wrappers) get
 * rest+hover by default. Pass `noHover: true` to suppress the hover layer,
 * mirroring the `<Button noHover>` prop.
 */
export type ButtonVariantOptions = VariantProps<typeof buttonRestVariants> & {
  noHover?: boolean;
  className?: string;
};

const buttonVariants = (opts: ButtonVariantOptions = {}): string => {
  const { noHover, className, ...rest } = opts;
  return cn(
    buttonRestVariants(rest),
    !noHover && buttonHoverVariants({ variant: rest.variant }),
    className,
  );
};

/**
 * Base button properties that extend HTML button attributes and include CVA variants.
 * This type provides the foundation for all button configurations.
 */
export type BaseButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonRestVariants> & {
    /**
     * Suppress the variant's hover styles. Use when the button sits inside
     * a container whose own hover already paints the affordance (table
     * rows, open Dropdown triggers, etc.) — without this you'd get a
     * double-flash on hover/leave.
     */
    noHover?: boolean;
  };

/**
 * Button properties when using asChild pattern.
 * When asChild is true, the button renders as a Slot component, allowing it to be
 * rendered as any HTML element or React component. In this mode, loading states
 * and icons are not supported as they require button-specific behavior.
 */
export type AsChildButtonProps = BaseButtonProps & {
  /** When true, renders the button as a Slot component instead of a button element */
  asChild: true;
};

/**
 * Extended button properties with additional features like loading states and icons.
 * This type is used when asChild is false, enabling full button functionality.
 */
export type ExtendedButtonProps = BaseButtonProps & {
  /** When false or undefined, enables full button functionality including loading states and icons */
  asChild?: false;

  /**
   * Shows a loading indicator and disables the button when true.
   * Only available when asChild is false.
   */
  isLoading?: boolean;

  /**
   * Icon element to display on the left side of the button text.
   * Only available when asChild is false.
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon element to display on the right side of the button text.
   * Only available when asChild is false.
   */
  rightIcon?: React.ReactNode;

  /**
   * Label to display when the button is in loading state.
   * Only available when asChild is false.
   */
  loadingLabel?: string;
};

/**
 * Union type representing all possible button configurations.
 * The type system ensures that loading states and icons can only be used
 * when asChild is false, maintaining proper component behavior.
 */
export type ButtonProps = ExtendedButtonProps | AsChildButtonProps;

/**
 * A versatile button component that supports multiple variants, sizes, and states.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Button>Click me</Button>
 *
 * // With loading state
 * <Button isLoading>Loading...</Button>
 *
 * // With icons
 * <Button leftIcon={<Icon />} rightIcon={<Arrow />}>
 *   Button with Icons
 * </Button>
 *
 * // As child (renders as anchor)
 * <Button asChild>
 *   <a href="/link">Link Button</a>
 * </Button>
 *
 * // Suppress hover when the surrounding container already paints one
 * <Button variant="tertiary" size="sm" noHover>
 *   <Icons.DotsVertical />
 * </Button>
 * ```
 *
 * @param props - Button configuration properties
 * @param props.variant - Visual style variant (default: 'primary')
 * @param props.size - Size variant (default: 'md')
 * @param props.fullWidth - Whether button should take full width
 * @param props.noHover - Disable the variant's hover transitions. Use when
 *   the button sits inside a container whose own hover already provides
 *   the affordance (table rows, open Dropdown triggers, etc.).
 * @param props.asChild - Whether to render as Slot component
 * @param props.isLoading - Shows loading state and disables button
 * @param props.leftIcon - Icon to display on the left
 * @param props.rightIcon - Icon to display on the right
 * @param props.className - Additional CSS classes
 * @param props.children - Button content
 * @param ref - Forwarded ref to the button element
 *
 * @returns A button element or Slot component based on asChild prop
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, noHover, asChild = false, ...props }, ref) => {
    const rootClassName = buttonVariants({
      variant,
      size,
      fullWidth,
      noHover,
      className,
    });

    if (asChild) {
      return (
        <Slot
          data-variant={variant}
          data-size={size}
          data-full-width={!!fullWidth}
          className={rootClassName}
          // explicitly mark it as `any` to tell that the passed ref is not a HTMLButtonElement
          ref={ref as React.Ref<any>}
          {...(props as AsChildButtonProps)}
        />
      );
    }

    const {
      isLoading,
      children,
      leftIcon,
      rightIcon,
      disabled,
      loadingLabel = 'Loading',
      ...restProps
    } = props as ExtendedButtonProps;

    const isDisabled = isLoading || disabled;
    const loadingIndicatorSize = size === 'sm' || size === 'xs' ? 16 : 20;

    return (
      <button
        className={rootClassName}
        ref={ref}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        aria-disabled={isDisabled || undefined}
        data-loading={isLoading || undefined}
        data-variant={variant}
        data-size={size}
        data-full-width={!!fullWidth}
        {...restProps}
      >
        {isLoading ? (
          <>
            <LoadingIndicator size={loadingIndicatorSize} />
            <span className="sr-only" aria-live="polite">
              {loadingLabel}
            </span>
          </>
        ) : null}
        {leftIcon}
        {children}
        {rightIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
